<script lang="ts">
import type { StudyPatientDemographicsSlideProps } from '$lib/components/study/slide-layout-types'
import StudyBlueCalloutCard from '../cards/StudyBlueCalloutCard.svelte'
import StudySlideDoughnutCard from '../cards/StudySlideDoughnutCard.svelte'
import StudySlideTextCard from '../cards/StudySlideTextCard.svelte'
import BarChart from '../charts/BarChart.svelte'
import StudySlide from '../StudySlide.svelte'
import StudySlideContent from '../StudySlideContent.svelte'

/** Age cohort bar + gender doughnut + narrative cards + insight (`s7`). */
let props: StudyPatientDemographicsSlideProps = $props()

const headlineStyle = $derived(props.headlineStyle ?? 'margin-bottom:.95rem;')
const tcStyle = $derived(props.tcStyle ?? 'gap:1.4rem;align-items:start;')
const ageY = $derived(props.ageYTickFormat ?? ((v: number) => v + 'M'))
const ageTip = $derived(props.ageTooltipFormat ?? ((v: number) => ` ${v.toFixed(1)}M claims`))
const gd = $derived(props.genderDoughnut)
const genderTotal = $derived(gd.data.reduce((sum, v) => sum + v, 0))
/** Percent per category, rounded to whole numbers and forced to sum to 100 so the bar never
 *  leaves a sliver of background showing from rounding drift. */
const genderParts = $derived(
  gd.data.map((value, index) => {
    const share = (v: number) => (genderTotal > 0 ? Math.round((v / genderTotal) * 100) : 0)
    const isLast = index === gd.data.length - 1
    const priorSum = gd.data.slice(0, index).reduce((sum, v) => sum + share(v), 0)
    return {
      label: gd.labels[index] ?? '',
      color: gd.colors[index] ?? '#94a3b8',
      // The zero-total guard has to cover the remainder branch too: `100 - priorSum` is 100 when
      // there is nothing to divide, which rendered the final category as a full-width bar.
      pct: genderTotal === 0 ? 0 : isLast ? Math.max(0, 100 - priorSum) : share(value),
    }
  }),
)
</script>

<StudySlide
  id={props.id}
  active={props.active}
  extraBottom={props.extraBottom}
  background={props.background}
  slideClass={props.slideClass}
>
  <StudySlideContent
    maxWidth={props.maxWidth ?? '980px'}
    contentClass={props.contentClass}
    style={props.contentStyle}
  >
    <div class="ey">{@html props.eyebrow}</div>
    <div class="hl" style={headlineStyle}>{@html props.headline}</div>
    <div class="tc" style={tcStyle}>
      <div style="display:flex;flex-direction:column;gap:.72rem;">
        <div class="cc">
          <div class="ct">{props.ageBarTitle}</div>
          <div class="cs">{@html props.ageBarSubtitle}</div>
          <BarChart
            active={props.active ?? false}
            height={props.ageChartHeight ?? '180px'}
            labels={props.ageLabels}
            datasets={[
              {
                data: props.ageBarData,
                backgroundColor: props.ageBarColors,
                borderRadius: 4,
              },
            ]}
            yTickFormat={ageY}
            tooltipFormat={ageTip}
          />
        </div>
        {#if props.genderAsSplitBar}
          <div>
            {#if gd.subtitle}
              <div class="cs" style="margin-bottom:.34rem;">{@html gd.subtitle}</div>
            {/if}
            <div
              style="display:flex;height:28px;border-radius:6px;overflow:hidden;font-size:.74rem;font-weight:700;"
            >
              {#each genderParts as part}
                <div
                  style="width:{part.pct}%;background:{part.color};color:#0b1220;display:flex;align-items:center;padding:0 .6rem;white-space:nowrap;"
                >
                  {part.label} &middot; {part.pct}%
                </div>
              {/each}
            </div>
          </div>
        {:else}
          <StudySlideDoughnutCard
            cardClass={gd.cardClass ?? ''}
            cardStyle={gd.cardStyle}
            active={props.active ?? false}
            title={gd.title}
            subtitle={gd.subtitle}
            labels={gd.labels}
            data={gd.data}
            colors={gd.colors}
            type={gd.type}
            cutout={gd.cutout}
            legendPosition={gd.legendPosition}
            tooltipFormat={gd.tooltipFormat}
            height={gd.height ?? '122px'}
          />
        {/if}
      </div>
      <div style="display:flex;flex-direction:column;gap:.72rem;">
        {#each props.narrativeCards as card}
          <StudySlideTextCard
            title={card.title}
            text={card.text}
            icon={card.icon}
            cardClass={card.cardClass ?? ''}
            cardStyle={card.cardStyle}
            titleClass={card.titleClass ?? ''}
            titleStyle={card.titleStyle}
            footerValue={card.footerValue}
            footerCaption={card.footerCaption}
          />
        {/each}
        {#if props.insightHtml}<StudyBlueCalloutCard bodyHtml={props.insightHtml} />{/if}
      </div>
    </div>
  </StudySlideContent>
</StudySlide>
